import React, { useState, useEffect } from "react";

import * as Common from "../commons/common";

import ReactApexChart from "react-apexcharts";
import moment from "moment";

export type ChartData = {
    name: string;
    data: ChartElement[];
    type?: string;
    stacked?: boolean;
};

export type ChartElement = {
    x: number | string;
    y: number | string;
};

function DataChart({
    height,
    tickAmount,
    series,
    option,
    onClickChart,
}: {
    height?: string;
    tickAmount?: number;
    series: ChartData[];
    option?: Record<string, any>;
    onClickChart?: (dataPointIndex: number) => void;
}) {
    const [chartOption, setChartOption] = useState<Record<string, any> | undefined>();

    const handleClick = (dataPointIndex: number) => {
        if (onClickChart !== undefined) onClickChart(dataPointIndex);
    };

    useEffect(() => {
        if (option === undefined) return;
        const optimalColumnWidthPercent = 20 + 60 / (1 + 30 * Math.exp(-series.length / 3));
        let zoomed = false;

        const chart_option = {
            ...option,
            chart: {
                background: "white",
                toolbar: {
                    show: false,
                    tools: {
                        download: false,
                        selection: false,
                        pan: false,
                        zoom: false,
                        zoomin: false,
                        zoomout: false,
                    },
                },
                zoom: {
                    enabled: true,
                    type: "x",
                },
                events: {
                    zoomed: function (chartContext: any, { xaxis, yaxis }: any) {
                        zoomed = true;
                    },
                    click: function (event: any, chartContext: any, config: any) {
                        if (zoomed) {
                            zoomed = false;
                            return;
                        }
                        if (config.seriesIndex === -1 || config.dataPointIndex === -1) return;

                        handleClick(config.dataPointIndex);
                    },
                },
                type: "bar",
                height: height ? height : "auto",
                ...option.chart,
            },
            theme: {
                palette: "palette3",
                ...option.theme,
            },
            plotOptions: {
                bar: { columnWidth: optimalColumnWidthPercent + "%", horizontal: false, borderRadius: 4 },
            },
            stroke: {
                width: [2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
            },
            fill: {
                opacity: 1,
            },
            dataLabels: {
                enabled: false,
            },

            title: {
                align: "center",
            },
            xaxis: {
                type: "number",
                labels: {
                    style: { cssClass: "fw-rg fs-xs fc-title1" },
                    formatter: function (val: string, index: number) {
                        return val.length > 4 ? val + "월" : val + "년";
                    },
                },
                tooltip: {
                    formatter: function (val: number, index: number) {
                        return val < 13 ? val + "월" : val + "년";
                    },
                },
                ...option.xaxis,
            },
            tooltip: {
                enable: false,
                marker: {
                    show: true,
                },
                x: {
                    show: false,
                },
                y: {
                    formatter: function (value: number, { series, seriesIndex, dataPointIndex, w }: any) {
                        return value;
                    },
                },
                ...option.tooltip,
            },
            legend: {
                showForSingleSeries: true,
            },
            noData: {
                text: "No Data",
                align: "center",
                verticalAlign: "middle",
                offsetX: 0,
                offsetY: 0,
                style: {
                    fontSize: "32px",
                    fontFamily: "Noto Sans KR",
                },
            },
            axisBorder: {
                show: true,
                height: 1,
                width: "100%",
            },
            markers: {
                size: [3, 3, 3],
                shape: "circle",
                ...option.markers,
            },
        };

        setChartOption(chart_option);
    }, [option]);

    return (
        <>
            {chartOption && (
                <ReactApexChart options={chartOption} series={series} type={chartOption.chart.type} width="100%" height={chartOption.chart.height} />
            )}
        </>
    );
}

export default DataChart;
